import { supabase } from '@/core/database/supabase';
import type { 
  MaintenancePlan, 
  MaintenancePlanChecklist, 
  MaintenancePlanResource, 
  PlanStats, 
  PlanFilters 
} from '../types/maintenance-plan.types';

export const MaintenancePlanService = {
  /**
   * Lấy danh sách kế hoạch bảo dưỡng
   */
  async getPlans(companyId: string, filters: PlanFilters = {}): Promise<{ data: MaintenancePlan[], total: number }> {
    let query = supabase
      .from('maintenance_plans')
      .select(`
        *,
        project:projects(id, name),
        contract:maintenance_contracts(id, contract_number),
        zone:project_zones(id, zone_name)
      `, { count: 'exact' })
      .eq('company_id', companyId);

    if (filters.status && filters.status !== 'all') query = query.eq('status_code', filters.status);
    if (filters.priority && filters.priority !== 'all') query = query.eq('priority_code', filters.priority);
    if (filters.projectId) query = query.eq('project_id', filters.projectId);
    if (filters.search) {
      query = query.or(`name.ilike.%${filters.search}%,code.ilike.%${filters.search}%`);
    }

    query = query.order('name', { ascending: true });

    const { data, error, count } = await query;

    if (error) throw error;
    return {
      data: (data || []) as MaintenancePlan[],
      total: count || 0
    };
  },

  /**
   * Chi tiết kế hoạch
   */
  async getPlanDetail(id: string): Promise<MaintenancePlan> {
    const { data, error } = await supabase
      .from('maintenance_plans')
      .select(`
        *,
        project:projects(id, name),
        contract:maintenance_contracts(id, contract_number),
        zone:project_zones(id, zone_name)
      `)
      .eq('id', id)
      .single();

    if (error) throw error;
    return data as MaintenancePlan;
  },

  /**
   * Lấy checklist của kế hoạch
   */
  async getPlanChecklists(planId: string): Promise<MaintenancePlanChecklist[]> {
    const { data, error } = await supabase
      .from('maintenance_plan_checklists')
      .select('*')
      .eq('plan_id', planId)
      .order('sequence_order', { ascending: true });

    if (error) throw error;
    return data as MaintenancePlanChecklist[];
  },

  /**
   * Lấy tài nguyên của kế hoạch
   */
  async getPlanResources(planId: string): Promise<MaintenancePlanResource[]> {
    const { data, error } = await supabase
      .from('maintenance_plan_resources')
      .select('*')
      .eq('plan_id', planId);

    if (error) throw error;
    return data as MaintenancePlanResource[];
  },

  /**
   * Thống kê kế hoạch
   */
  async getPlanStats(companyId: string): Promise<PlanStats> {
    const { data, error } = await supabase
      .from('maintenance_plans')
      .select('status_code, estimated_hours')
      .eq('company_id', companyId);

    if (error) throw error;

    const stats: PlanStats = {
      totalPlans: data.length,
      activePlans: data.filter(p => p.status_code === 'active').length,
      pendingApproval: data.filter(p => p.status_code === 'pending_approval').length,
      expiredPlans: data.filter(p => p.status_code === 'expired').length,
      totalEstimatedHours: data.reduce((acc, p) => acc + (p.estimated_hours || 0), 0),
    };

    return stats;
  },

  /**
   * CRUD Operations
   */
  async createPlan(data: Partial<MaintenancePlan>): Promise<MaintenancePlan> {
    const { data: result, error } = await supabase
      .from('maintenance_plans')
      .insert([data])
      .select()
      .single();

    if (error) throw error;
    return result as MaintenancePlan;
  },

  async updatePlan(id: string, updates: Partial<MaintenancePlan>): Promise<MaintenancePlan> {
    const { data, error } = await supabase
      .from('maintenance_plans')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data as MaintenancePlan;
  }
};
